import { ReactNode } from 'react';

import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';

import { UrlPath } from '@/app/@types/urlPath';
import { AuthContextValue } from '@/app/contexts/AuthContext';
import { useActiveRoute } from '@/app/hooks/useActiveRoute';
import { useAuth } from '@/app/hooks/useAuth';
import { saveNavigationMetadata } from '@/app/utils/cookies/navigation';
import { Header } from '@/view/components/features/header';
import { SidebarNavigation } from '@/view/components/features/sidebar/sidebar-navigation';
import { useAllowedFeatures } from '@/view/hooks/useAllowedFeatures';

interface NavigationMetadata {
  title: string;
  pathname: string;
  fullPath: string;
}

interface AppLayoutProps {
  children: ReactNode;
  showSidebar?: boolean;
  showHeader?: boolean;
}

interface MainContentProps {
  showSidebar: boolean;
  currentPath: UrlPath;
  programId: string | null;
}

const FULL_SCREEN_PAGES: Map<UrlPath, boolean> = new Map([
  ['/$programId/eprod/$scriptId/painel', true],
]);

const useNavigationMetadata = () => {
  const { currentPath, pathname, fullPath, context } = useActiveRoute();

  const title = context?.title;

  if (pathname && fullPath && title) {
    const metadata = { title, pathname, fullPath } satisfies NavigationMetadata;
    saveNavigationMetadata(metadata);
  }

  return { currentPath };
};

const AppLayout = ({ children, showHeader }: AppLayoutProps) => (
  <div className="h-screen overflow-hidden flex flex-col">
    {showHeader && <Header />}
    <div className="flex flex-col mb:flex-row w-full flex-grow mx-auto border border-border-neutral overflow-hidden">
      {children}
    </div>
  </div>
);

const MainContent = ({ showSidebar, currentPath, programId }: MainContentProps) => {
  const { allowedFeatures } = useAllowedFeatures();
  return (
    <>
      {showSidebar && allowedFeatures.length > 0 && !!programId && (
        <SidebarNavigation currentPath={currentPath} programId={programId} />
      )}
      <Outlet />
    </>
  );
};

const Protected = () => {
  const { currentPath } = useNavigationMetadata();
  const { selectedProduct } = useAuth();

  const isFullScreenPage = Boolean(FULL_SCREEN_PAGES.get(currentPath as UrlPath));
  const showDefaultComponents = !isFullScreenPage;

  return (
    <AppLayout showHeader={showDefaultComponents}>
      <MainContent
        currentPath={currentPath || '/'}
        programId={selectedProduct}
        showSidebar={showDefaultComponents}
      />
    </AppLayout>
  );
};

const redirectToLogin = (redirectUrl: string): never => {
  throw redirect({
    to: '/',
    search: { redirect: redirectUrl },
  });
};

const hasPermissionForPath = (auth: AuthContextValue, path: string) => {
  const idProduction = path.split('/')[1];
  const actualProduction = auth.allowedProductions.find(
    (production) => production.code === idProduction,
  );
  if (!actualProduction) {
    return auth.allowedProductions[0].resources?.find((resource) => resource.path?.includes(path));
  }

  const newPath = path
    .split('/')
    .map((item, index) => {
      if (index == 1) {
        return '$programId';
      }
      return item;
    })
    .join('/');

  const hasPermissionPath = actualProduction?.resources?.find((resource) =>
    resource.path?.includes(newPath),
  );
  return !!hasPermissionPath;
};

export const Route = createFileRoute('/_protected')({
  beforeLoad: ({ context, location }) => {
    const { auth } = context;

    if (!auth.signedIn) {
      redirectToLogin(location.href);
    }

    const path = location.pathname;

    if (!hasPermissionForPath(auth, path)) {
      throw redirect({ to: '/forbidden', search: { from: path } });
    }
  },
  component: Protected,
});
